import React, { useRef, useMemo, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { Bird } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";

// ----------------------------------------------------------------------------
// KAITY'S OWL SHRINE  (office in-joke — intentionally self-contained so it can be
// deleted later by removing its <OwlShrine /> line in IsoWorld.js, the OWL_SHRINE
// import in Home.js / IsoWorld.js, and this file. It is NOT in the DB or the nav,
// so deleting it leaves the real city untouched.)
// ----------------------------------------------------------------------------

// Meta used so the shrine can be focused (camera fly-in) and show an info panel
// just like a real building — without ever entering the buildings list / nav.
export const OWL_SHRINE = {
  slug: "kaity-owl-shrine",
  name: "Kaity's Owl Shrine",
  category: "Strictly Unofficial",
  description:
    "A shrine to Kaity and the owls she loves. She does her sharpest thinking after dark, can rotate her head a clean 200° to dodge a meeting, and ends every debate with one slow, devastating blink. The little ones perched below are her parliament — yes, that is genuinely the word for a group of owls. Approach with snacks.",
  color: "#27424F",
  accent: BRAND.yellow,
  // layout / framing
  positionX: -29,
  positionZ: -29,
  height: 11,
  width: 5,
  depth: 5,
  shape: "owl",
};

// A tiny stylised owl — one of "the owls she loves". Cute, glowing-eyed companion.
function MiniOwl({ position = [0, 0, 0], scale = 1, rotationY = 0, night }) {
  const core = night ? "#23272E" : "#2B2E34";
  return (
    <group position={position} rotation={[0, rotationY, 0]} scale={scale}>
      {/* body */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <sphereGeometry args={[0.52, 16, 14]} />
        <meshStandardMaterial color={core} roughness={0.5} metalness={0.2} />
      </mesh>
      {/* head */}
      <mesh position={[0, 1.0, 0]} castShadow>
        <sphereGeometry args={[0.42, 16, 14]} />
        <meshStandardMaterial color={core} roughness={0.5} metalness={0.2} />
      </mesh>
      {/* eyes */}
      {[-0.18, 0.18].map((x) => (
        <mesh key={x} position={[x, 1.06, 0.36]}>
          <circleGeometry args={[0.13, 16]} />
          <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 1.5 : 0.9} toneMapped={false} />
        </mesh>
      ))}
      {/* pupils */}
      {[-0.18, 0.18].map((x) => (
        <mesh key={`p${x}`} position={[x, 1.06, 0.38]}>
          <circleGeometry args={[0.05, 12]} />
          <meshStandardMaterial color={BRAND.charcoal} />
        </mesh>
      ))}
      {/* ear tufts */}
      {[-1, 1].map((sx) => (
        <mesh key={sx} position={[sx * 0.24, 1.36, -0.02]} rotation={[0, 0, sx * 0.4]} castShadow>
          <coneGeometry args={[0.1, 0.3, 5]} />
          <meshStandardMaterial color={core} roughness={0.5} />
        </mesh>
      ))}
      {/* beak */}
      <mesh position={[0, 0.9, 0.4]} rotation={[Math.PI * 0.6, 0, 0]}>
        <coneGeometry args={[0.07, 0.2, 4]} />
        <meshStandardMaterial color={BRAND.yellow} roughness={0.4} metalness={0.2} />
      </mesh>
    </group>
  );
}

export default function OwlShrine({
  selected = null,
  onSelect = () => {},
  position = [OWL_SHRINE.positionX, 0, OWL_SHRINE.positionZ],
  rotationY = Math.PI / 4,
  scale = 1.2,
}) {
  const night = useNight();
  const orb = useRef();
  const haloRef = useRef();
  const eyeL = useRef();
  const eyeR = useRef();
  const headRef = useRef();
  const eyeGroupL = useRef();
  const eyeGroupR = useRef();
  const head = useRef({ cur: 0, tgt: 0, t: 0, dur: 2 + Math.random() * 3, ease: 0.5 + Math.random() * 0.6, tilt: 0, tiltTgt: 0 });
  const blink = useRef({ t: 0, next: 1.5 + Math.random() * 3, active: false, ph: 0, dbl: false });
  const [hovered, setHovered] = useState(false);

  const active = selected === OWL_SHRINE.slug;
  const dimmed = !!selected && !active;

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    const p = Math.sin(t * 2.0) * 0.5 + 0.5;
    if (orb.current) {
      orb.current.material.emissiveIntensity = (night ? 1.8 : 1.2) + p * 0.9;
      orb.current.scale.setScalar(1 + p * 0.08);
    }
    const eyeI = (night ? 1.7 : 1.05) + p * 0.7;
    if (eyeL.current) eyeL.current.material.emissiveIntensity = eyeI;
    if (eyeR.current) eyeR.current.material.emissiveIntensity = eyeI;
    if (haloRef.current) haloRef.current.rotation.z = t * 0.6;

    // --- RANDOM, SLOW owl head crane (no fixed cadence, eased toward fresh random
    //     targets after random holds — so it never feels robotic) ---
    if (headRef.current) {
      const hs = head.current;
      hs.t += dt;
      if (hs.t >= hs.dur) {
        hs.t = 0;
        hs.dur = 2.4 + Math.random() * 4.5; // random hold before the next move
        const r = Math.random();
        hs.tgt =
          r < 0.4
            ? Math.random() * 0.5 - 0.25 // often settle near forward
            : (Math.random() < 0.5 ? 1 : -1) * (0.7 + Math.random() * 1.2); // big crane
        hs.ease = 0.5 + Math.random() * 0.7; // random (gentle) turn speed
        hs.tiltTgt = (Math.random() * 2 - 1) * 0.12; // small inquisitive tilt
      }
      hs.cur += (hs.tgt - hs.cur) * Math.min(1, dt * hs.ease);
      hs.tilt += (hs.tiltTgt - hs.tilt) * Math.min(1, dt * 0.8);
      headRef.current.rotation.y = hs.cur;
      headRef.current.rotation.z = hs.tilt;
    }

    // --- RANDOM blink (random intervals, occasional double-blink) ---
    const bk = blink.current;
    let open = 1;
    bk.t += dt;
    if (!bk.active && bk.t >= bk.next) {
      bk.active = true;
      bk.ph = 0;
      bk.dbl = Math.random() < 0.3;
    }
    if (bk.active) {
      bk.ph += dt;
      const d1 = 0.22;
      if (bk.ph < d1) {
        open = 1 - Math.sin((bk.ph / d1) * Math.PI) * 0.92;
      } else if (bk.dbl && bk.ph < d1 + 0.12 + d1) {
        const p2 = bk.ph - (d1 + 0.12);
        open = p2 > 0 ? 1 - Math.sin((p2 / d1) * Math.PI) * 0.9 : 1;
      } else {
        bk.active = false;
        bk.t = 0;
        bk.next = 1.8 + Math.random() * 4.5;
      }
    }
    if (eyeGroupL.current) eyeGroupL.current.scale.y = open;
    if (eyeGroupR.current) eyeGroupR.current.scale.y = open;
  });

  const setCursor = (on) => {
    setHovered(on);
    if (typeof document !== "undefined") document.body.style.cursor = on ? "pointer" : "auto";
  };

  const glass = (extra = {}) => (
    <meshStandardMaterial
      color={night ? "#1B2A33" : "#27424F"}
      transparent
      opacity={dimmed ? 0.32 : 0.5}
      roughness={0.12}
      metalness={0.6}
      emissive={"#2E6486"}
      emissiveIntensity={night ? 0.45 : 0.12}
      {...extra}
    />
  );
  const core = night ? "#23272E" : "#2B2E34";
  const featherA = night ? "#2A2F37" : "#3A3E46";
  const featherB = night ? "#333944" : "#4A4F59";
  const trim = night ? "#9FB3C8" : "#D9D3C4";

  const featherRows = useMemo(() => {
    const rows = [
      { y: 2.05, n: 4, spread: 1.0, z: 1.58, s: 0.4 },
      { y: 2.75, n: 5, spread: 1.3, z: 1.74, s: 0.46 },
      { y: 3.45, n: 4, spread: 1.1, z: 1.62, s: 0.42 },
      { y: 4.05, n: 3, spread: 0.78, z: 1.35, s: 0.36 },
    ];
    const arr = [];
    rows.forEach((r, ri) => {
      for (let i = 0; i < r.n; i++) {
        const x = (i - (r.n - 1) / 2) * (r.spread / Math.max(1, r.n - 1)) * 2;
        arr.push({ x, y: r.y, z: r.z, s: r.s, c: (ri + i) % 2 ? featherA : featherB, key: `${ri}-${i}` });
      }
    });
    return arr;
  }, [featherA, featherB]);

  return (
    <group position={position}>
      {/* clickable hit proxy (covers the whole shrine) */}
      <mesh
        position={[0, 6, 0]}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(OWL_SHRINE.slug);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setCursor(true);
        }}
        onPointerOut={() => setCursor(false)}
      >
        <boxGeometry args={[6, 12.5, 6]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* active / hover ground ring */}
      {(active || hovered) && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.14, 0]}>
          <ringGeometry args={[3.2, 3.7, 56]} />
          <meshBasicMaterial color={BRAND.yellow} transparent opacity={active ? 0.85 : 0.4} depthWrite={false} />
        </mesh>
      )}

      {/* ===== ROTATED + SCALED OWL ===== */}
      <group rotation={[0, rotationY, 0]} scale={scale}>
        {/* ---- stone shrine pedestal ---- */}
        <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[2.7, 2.95, 0.6, 36]} />
          <meshStandardMaterial color={night ? "#3A3F49" : "#B7B2A4"} roughness={1} />
        </mesh>
        <mesh position={[0, 0.74, 0]} castShadow>
          <cylinderGeometry args={[2.2, 2.4, 0.3, 36]} />
          <meshStandardMaterial color={night ? "#2E333B" : "#9A968C"} roughness={1} />
        </mesh>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.9, 0]}>
          <ringGeometry args={[1.78, 2.0, 36]} />
          <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.5 : 0.18} roughness={0.5} side={THREE.DoubleSide} />
        </mesh>

        {/* ---- perch branch + taloned feet ---- */}
        <mesh position={[0, 1.0, 0.2]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.16, 0.18, 2.4, 8]} />
          <meshStandardMaterial color={night ? "#3a2f24" : "#6B5640"} roughness={1} />
        </mesh>
        {[-0.62, 0.62].map((fx) => (
          <group key={`foot${fx}`} position={[fx, 1.05, 0.36]}>
            <mesh castShadow><sphereGeometry args={[0.26, 12, 10]} /><meshStandardMaterial color={core} roughness={0.5} /></mesh>
            {[-0.32, 0, 0.32].map((tx, ti) => (
              <mesh key={ti} position={[tx * 0.6, -0.16, 0.26]} rotation={[1.1, 0, tx]} castShadow>
                <coneGeometry args={[0.07, 0.42, 6]} />
                <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.4 : 0.12} roughness={0.4} metalness={0.2} />
              </mesh>
            ))}
          </group>
        ))}

        {/* ---- BODY ---- */}
        <mesh position={[0, 3.0, 0]} scale={[1, 1.28, 0.95]} castShadow>
          <sphereGeometry args={[1.84, 28, 24]} />
          <meshStandardMaterial color={core} roughness={0.5} metalness={0.2} transparent opacity={dimmed ? 0.55 : 1} />
        </mesh>
        <mesh position={[0, 3.0, 0]} scale={[1, 1.28, 0.95]} castShadow>
          <sphereGeometry args={[2.0, 28, 24]} />
          {glass()}
        </mesh>
        {[1.9, 2.7, 3.5].map((y, i) => (
          <mesh key={`band${i}`} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[1.96 - Math.abs(y - 3.0) * 0.18, 0.05, 8, 40]} />
            <meshStandardMaterial color={trim} roughness={0.5} metalness={0.2} />
          </mesh>
        ))}

        {/* feathered chest */}
        {featherRows.map((f) => (
          <mesh key={`fe${f.key}`} position={[f.x, f.y, f.z]} scale={[1, 0.62, 0.5]} castShadow>
            <sphereGeometry args={[f.s, 12, 10]} />
            <meshStandardMaterial color={f.c} roughness={0.7} metalness={0.15} flatShading />
          </mesh>
        ))}

        {/* ---- WINGS ---- */}
        {[-1, 1].map((sx) => (
          <group key={`wing${sx}`} position={[sx * 1.92, 3.05, -0.05]} rotation={[0, 0, sx * 0.16]}>
            {[0, 1, 2].map((i) => (
              <group key={i} position={[0, 0.5 - i * 0.7, 0]}>
                <mesh castShadow>
                  <capsuleGeometry args={[0.32, 1.5 - i * 0.18, 6, 12]} />
                  {glass({ opacity: dimmed ? 0.4 : 0.6 })}
                </mesh>
                <mesh position={[0, -(0.95 - i * 0.1), 0]} castShadow>
                  <coneGeometry args={[0.3, 0.5, 6]} />
                  <meshStandardMaterial color={featherA} roughness={0.6} metalness={0.15} />
                </mesh>
              </group>
            ))}
          </group>
        ))}

        {/* ---- HEAD (cranes left/right like an owl) ---- */}
        <group ref={headRef} position={[0, 5.45, 0]}>
          <mesh castShadow><sphereGeometry args={[1.42, 28, 24]} /><meshStandardMaterial color={core} roughness={0.5} metalness={0.2} transparent opacity={dimmed ? 0.55 : 1} /></mesh>
          <mesh castShadow><sphereGeometry args={[1.56, 28, 24]} />{glass()}</mesh>

          {[-0.5, 0.5].map((x) => (
            <mesh key={`disc${x}`} position={[x, 0.08, 1.14]} rotation={[Math.PI / 2, 0, 0]} castShadow>
              <cylinderGeometry args={[0.92, 0.92, 0.16, 28]} />
              <meshStandardMaterial color={trim} roughness={0.4} metalness={0.25} transparent opacity={0.9} />
            </mesh>
          ))}

          {[-0.5, 0.5].map((x, i) => (
            <group key={`eye${x}`} ref={i === 0 ? eyeGroupL : eyeGroupR} position={[x, 0.12, 1.28]}>
              <mesh rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[0.5, 0.5, 0.1, 28]} />
                <meshStandardMaterial color={core} roughness={0.5} metalness={0.3} />
              </mesh>
              <mesh ref={i === 0 ? eyeL : eyeR} position={[0, 0, 0.06]}>
                <circleGeometry args={[0.4, 28]} />
                <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={1.1} toneMapped={false} />
              </mesh>
              <mesh position={[0, 0, 0.085]}>
                <circleGeometry args={[0.17, 20]} />
                <meshStandardMaterial color={BRAND.charcoal} />
              </mesh>
              <mesh position={[0.08, 0.08, 0.1]}>
                <circleGeometry args={[0.05, 12]} />
                <meshBasicMaterial color={"#ffffff"} />
              </mesh>
            </group>
          ))}

          {[-1, 1].map((sx) => (
            <mesh key={`brow${sx}`} position={[sx * 0.5, 0.62, 1.18]} rotation={[0, 0, sx * 0.5]} castShadow>
              <boxGeometry args={[0.85, 0.16, 0.22]} />
              <meshStandardMaterial color={core} roughness={0.5} metalness={0.2} />
            </mesh>
          ))}

          <mesh position={[0, -0.18, 1.34]} rotation={[Math.PI * 0.62, 0, 0]} castShadow>
            <coneGeometry args={[0.2, 0.62, 4]} />
            <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.5 : 0.15} roughness={0.4} metalness={0.25} />
          </mesh>

          {[-1, 1].map((sx) => (
            <group key={`ear${sx}`} position={[sx * 0.86, 1.16, -0.05]} rotation={[0, 0, sx * 0.42]}>
              <mesh castShadow><coneGeometry args={[0.3, 0.8, 5]} /><meshStandardMaterial color={core} roughness={0.5} metalness={0.2} /></mesh>
              <mesh position={[sx * 0.12, 0.55, 0]} rotation={[0, 0, sx * 0.2]} castShadow><coneGeometry args={[0.18, 0.55, 5]} /><meshStandardMaterial color={featherA} roughness={0.55} metalness={0.15} /></mesh>
            </group>
          ))}
        </group>

        {/* ---- LANTERN ---- */}
        <group position={[0, 7.05, 0]}>
          <mesh position={[0, 0.25, 0]}>
            <cylinderGeometry args={[0.07, 0.07, 1.0, 8]} />
            <meshStandardMaterial color={BRAND.charcoal} metalness={0.4} roughness={0.3} />
          </mesh>
          <mesh ref={orb} position={[0, 1.1, 0]}>
            <sphereGeometry args={[0.56, 18, 16]} />
            <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={1.4} toneMapped={false} roughness={0.2} />
          </mesh>
          <mesh ref={haloRef} position={[0, 1.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.86, 0.045, 8, 44]} />
            <meshBasicMaterial color={BRAND.yellow} transparent opacity={0.55} depthWrite={false} />
          </mesh>
          <pointLight position={[0, 1.1, 0]} color={"#FFE9A8"} intensity={night ? 2.2 : 1.2} distance={18} decay={2} />
        </group>

        {/* ---- THE OWLS SHE LOVES (her little parliament) ---- */}
        <MiniOwl position={[1.4, 1.1, 0.35]} scale={0.34} rotationY={-0.5} night={night} />
        <MiniOwl position={[-1.55, 0.95, 1.5]} scale={0.28} rotationY={0.6} night={night} />
        <MiniOwl position={[3.25, 0, 1.7]} scale={0.46} rotationY={-0.9} night={night} />
        <MiniOwl position={[-3.1, 0, -0.6]} scale={0.4} rotationY={1.6} night={night} />
      </group>

      {/* ===== floating label (screen-facing, constant size) ===== */}
      <Html position={[0, 7.8 * scale, 0]} center zIndexRange={[18, 0]} style={{ pointerEvents: "none" }}>
        <div
          data-testid="owl-shrine-label"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: BRAND.charcoal, color: "#fff",
            fontFamily: "'Archivo Narrow', Impact, sans-serif",
            fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            fontSize: 12, padding: "4px 10px", borderRadius: 7, whiteSpace: "nowrap",
            boxShadow: "0 6px 16px rgba(0,0,0,0.28)",
            border: "1px solid rgba(255,255,255,0.14)",
            opacity: dimmed ? 0.45 : 1,
          }}
        >
          <Bird size={13} strokeWidth={2.4} color={BRAND.yellow} />
          Kaity's Owl Shrine
        </div>
      </Html>
    </group>
  );
}
